import React, { useEffect, useMemo, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import moment from 'moment';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import { get, isEmpty, uniqueId } from 'lodash';
import { createStructuredSelector } from 'reselect';
import { Listings } from 'containers/AppContainer/typings';
import { makeSelectListings } from 'containers/AppContainer/selectors';
import { makeSelectSearchClaims } from 'containers/IdentificationPage/selectors';
import { IUploadedDocument } from 'containers/LawyerLegalProceedingsPage/typings';
import {
  makeSelectDocument,
  makeSelectDocumentId,
} from 'containers/UploadDocuments/selectors';
import Stepper from 'containers/AppContainer/Stepper';
import ToggleTabs from 'components/ToggleTabs';
import { translate } from 'containers/services/translate';
import messages from 'containers/BasicManagement/messages';
import {
  resetUploadDocumentStore,
  setDocumentId,
  uploadDocument,
} from 'containers/UploadDocuments/actions';
import { Container } from 'containers/BasicManagement/styled-components';
import JudgementAddForm from 'containers/BasicManagement/components/JudgementAddForm';
import CivilRequest from 'containers/BasicManagement/components/CivilRequest';
import AppealAddForm from 'containers/BasicManagement/components/AppealAddForm';
import ExpertReportForm from 'containers/BasicManagement/components/ExpertReportForm';
import ExpertReportAppealForm from 'containers/BasicManagement/components/ExpertReportAppealForm';
import {
  useAppealAdd,
  useExpertReport,
  useJudgementAdd,
  useJudgementOnMerits,
  useJudgementOnMeritsAppeal,
} from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/hooks';
import JudgementOnMeritsForm from 'containers/BasicManagement/components/JudgementOnMeritsForm';
import JudgementOnMeritsAppealForm from 'containers/BasicManagement/components/JudgementOnMeritsAppealForm';
import NavigationButtons from 'components/navigationButtons';
import { pushTo } from 'containers/services/routes-helper';
import { PAGES } from 'routes';
import { makeSelecClaimToPreopen } from 'containers/PreOpeningPage/selectors';
import { requestPreOpenBasicClaim } from 'containers/BasicManagement/redux/actions';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { PROCEDURE_STATUSES, PROCEDURE_STEPS } from 'containers/data-structure';
import {
  LEGAL_PROCEEDINGS_STEPS,
  PROCEDURE_TYPES,
} from 'containers/LawyerLegalProceedingsPage/constants';
import InvoiceFeeNoteDialog from 'components/InvoiceFeeNoteDialog';
import InvoiceInfoDialog from 'components/InvoiceFeeNoteDialog/InvoiceInfoDialog';
import {
  isCivilJudgementDefined,
  resolveJudiciaryProcessType,
} from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/utils';
import { formatCourtFileNumber } from 'containers/LawyerLegalProceedingsPage/judgementAdd/utils';
import { isAllValuesIsNil } from 'helpers/utils';
export interface JudiciaryProcessPageProps {
  intl: IntlShape;
  listings: Listings;
  policyInfo: any;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadDocumentStore: Function;
  isCivilProceeding: boolean;
  location: any;
  documentId: any;
  setDocumentId: Function;
  storedClaimToPreopen: any;
  requestPreOpenBasicClaim: Function;
  requestGetDocByDocumentumId: Function;
}

export const JudiciaryProcessPage: React.FC<JudiciaryProcessPageProps> = (
  props,
): JSX.Element => {
  const victimsWithIds = useMemo(() => {
    return get(props, 'location.state.victims', []).map((el: any) => {
      return { ...el, id: Number(uniqueId()) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.location.state.victims]);

  const vehiculesWithIds = useMemo(() => {
    return get(props, 'location.state.vehicules', []).map((el: any) => {
      return { ...el, id: Number(uniqueId()) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.location.state.vehicules]);

  const location = useLocation() as any;
  const [procedureInstance, setProcedureInstance] = useState<any>({
    procedureNatureId: get(location, 'state.procedureNatureId'),
    procedureType: get(location, 'state.procedureType'),
    judicialProcedureId: get(location, 'state.judicialProcedureId'),
    claimId: uniqueId(),
    claimNumber: '',
    judgementAddDocuments: [],
    expertiseReport: {},
    judgementAddAppealDocuments: [],
    expertReportAppeal: {},
    judgementOnMerits: {},
    judgementOnMeritsAppealInfo: {},
  });
  const {
    details,
    setDetails,
    processInformationValidation,
    messageError,
    civilPartyDocuments,
    setCivilPartyDocuments,
    areProcedureInformationValidated,
    judgementAddDocuments,
    setJudgementAddDocuments,
    isCivilPartyPresent,
  } = useJudgementAdd(props.policyInfo, props.intl);

  const { expertReportState, setExpertReportState } = useExpertReport(
    get(procedureInstance, 'judgementAddDocuments', []),
    get(procedureInstance, 'expertiseReport', {}),
  );

  let victimExpertises = [] as any;
  let vehiculeExpertises = [] as any;

  expertReportState.forEach((expState: any) => {
    get(expState, 'expertises', []).forEach((exp: any) => {
      victimExpertises = [
        ...victimExpertises,
        ...get(exp, 'victims', []).map((v: any) => {
          return get(v, 'victimExpertise', {});
        }),
      ];
      vehiculeExpertises = [
        ...vehiculeExpertises,
        ...get(exp, 'vehicules', []).map((v: any) => {
          return get(v, 'vehiculeExpertise', {});
        }),
      ];
    });
  });

  const {
    judgementOnMeritsInfo,
    setJudgementOnMeritsInfo,
  } = useJudgementOnMerits();

  const {
    judgementOnMeritsAppealInfo,
    setJudgementOnMeritsAppealInfo,
  } = useJudgementOnMeritsAppeal();

  const {
    detailsAppealAdd,
    setDetailsAppealAdd,
    processInformationValidationAppealAdd,
    messageErrorAppealAdd,
    civilPartyDocumentsAppealAdd,
    setCivilPartyDocumentsAppealAdd,
    areProcedureInformationValidatedAppealAdd,
    appealAddDocuments,
    setAppealAddDocuments,
    isCivilPartyPresentAppealAdd,
  } = useAppealAdd(props.policyInfo, props.intl);
  const commonLabels = [
    translate(messages.judgementAddLabelTitle, props),
    translate(messages.expertReportLabelTitle, props),
    translate(messages.judgementOnMeritsLabelTitle, props),
    translate(messages.appealAddLabelTitle, props),
    translate(messages.expertReportAppealLabelTitle, props),
    translate(messages.appealOnMeritsLabelTitle, props),
  ];

  const judiciaryProcesslabels =
    get(procedureInstance, 'procedureNatureId') !== 3
      ? [translate(messages.civilRequestLabelTitle, props), ...commonLabels]
      : [...commonLabels];

  const mappedLabels = judiciaryProcesslabels.filter(
    (label: string, index: number) => {
      const selectedJudiciaryStep = get(
        props,
        'listings.judicialProcedures',
        [],
      ).find(
        (el: any) => el.id === get(procedureInstance, 'judicialProcedureId'),
      );
      const selectedLabelTitle = get(
        selectedJudiciaryStep,
        'title',
        '',
      ).toLowerCase();
      const foundIndex = commonLabels.findIndex(
        (el: string) => el.toLowerCase() === selectedLabelTitle,
      );
      return index <= foundIndex;
    },
  );

  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);

  const [isSuccessPopinOpen, setIsSuccessPopinOpen] = useState<boolean>(false);
  const [
    decisionGenerationFeeNote,
    setDecisionGenerationFeeNote,
  ] = React.useState(null);

  const [
    openInvoiceFeeNoteInfoDialog,
    setOpenInvoiceFeeNoteInfoDialog,
  ] = React.useState(false);

  const [invoiceNumber, setInvoiceNumber] = React.useState(null);
  const [invoiceDate, setInvoiceDate] = React.useState(null);
  const [invoiceDocument, setInvoiceDocument] = React.useState({});

  const handleCloseSuccessDialog = () => {
    setIsSuccessPopinOpen(false);
    handleValidate(false);
  };

  const handleOpenInvoicesInfoDialog = () => {
    setIsSuccessPopinOpen(false);
    setOpenInvoiceFeeNoteInfoDialog(true);
  };

  const [lawyerFirmRef, setLawyerFirmRef] = React.useState(null);

  const handleCloseInvoiceFeeNote = () => {
    setOpenInvoiceFeeNoteInfoDialog(false);
  };

  const handleValidateInvoice = () => {
    handleValidate(true);
    setOpenInvoiceFeeNoteInfoDialog(false);
  };

  useEffect(() => {
    if (props.resetUploadDocumentStore) props.resetUploadDocumentStore();
    if (isEmpty(props.storedClaimToPreopen)) {
      pushTo(PAGES.LAWYER_HOME)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setProcedureInstance({
      ...procedureInstance,
      judgementAddDocuments: [...judgementAddDocuments],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [judgementAddDocuments]);

  const judiciaryProcessResolver = (judiciaryProcesslabels: any) => {
    return judiciaryProcesslabels.map((label: any) => {
      switch (label) {
        case translate(messages.civilRequestLabelTitle, props):
          return (
            <CivilRequest
              listings={props.listings}
              policyInfo={props.policyInfo}
              setProcedureInstance={setProcedureInstance}
              requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
            />
          );
        case translate(messages.judgementAddLabelTitle, props):
          return (
            <JudgementAddForm
              procedureInstance={procedureInstance}
              listings={props.listings}
              policyInfo={props.policyInfo}
              uploadDocument={props.uploadDocument}
              uploadedDocument={props.uploadedDocument}
              resetUploadDocumentStore={props.resetUploadDocumentStore}
              isCivilProceeding={props.isCivilProceeding}
              setProcedureInstance={setProcedureInstance}
              victims={victimsWithIds}
              vehicules={vehiculesWithIds}
              details={details}
              setDetails={setDetails}
              processInformationValidation={processInformationValidation}
              messageError={messageError}
              civilPartyDocuments={civilPartyDocuments}
              setCivilPartyDocuments={setCivilPartyDocuments}
              canStartCivilParty={areProcedureInformationValidated}
              judgementAddDocuments={judgementAddDocuments}
              setJudgementAddDocuments={setJudgementAddDocuments}
              disabled={
                !props.isCivilProceeding
                  ? !areProcedureInformationValidated
                  : isCivilPartyPresent
              }
              requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
            />
          );
        case translate(messages.expertReportLabelTitle, props):
          return (
            <ExpertReportForm
              procedureInstance={procedureInstance}
              listings={props.listings}
              policyInfo={props.policyInfo}
              uploadDocument={props.uploadDocument}
              uploadedDocument={props.uploadedDocument}
              resetUploadDocumentStore={props.resetUploadDocumentStore}
              judgementAddDocuments={get(
                procedureInstance,
                'judgementAddDocuments',
                [],
              )}
              expertiseReport={get(procedureInstance, 'expertiseReport', {})}
              setProcedureInstance={setProcedureInstance}
              documentId={props.documentId}
              setDocumentId={props.setDocumentId}
              expertReportState={expertReportState}
              setExpertReportState={setExpertReportState}
              requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
            />
          );
        case translate(messages.judgementOnMeritsLabelTitle, props):
          return (
            <JudgementOnMeritsForm
              listings={props.listings}
              policyInfo={props.policyInfo}
              judgementAddDocuments={get(
                procedureInstance,
                'judgementAddDocuments',
                [],
              )}
              judgementOnMerits={get(
                procedureInstance,
                'judgementOnMerits',
                [],
              )}
              claimId={get(procedureInstance, 'claimId')}
              claimNumber={get(procedureInstance, 'claimNumber')}
              uploadDocument={props.uploadDocument}
              uploadedDocument={props.uploadedDocument}
              resetUploadDocumentStore={props.resetUploadDocumentStore}
              setDocumentId={props.setDocumentId}
              documentId={props.documentId}
              judgementOnMeritsInfo={judgementOnMeritsInfo}
              setJudgementOnMeritsInfo={setJudgementOnMeritsInfo}
              procedureNatureId={get(procedureInstance, 'procedureNatureId')}
              judgementAdd={civilPartyDocuments}
              procedureInstance={procedureInstance}
              victims={victimsWithIds}
              vehicules={vehiculesWithIds}
              expertReport={{
                vehiculeExpertises,
                victimExpertises,
              }}
              requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
            />
          );
        case translate(messages.appealAddLabelTitle, props):
          return (
            <AppealAddForm
              procedureInstance={procedureInstance}
              listings={props.listings}
              policyInfo={props.policyInfo}
              uploadDocument={props.uploadDocument}
              uploadedDocument={props.uploadedDocument}
              resetUploadDocumentStore={props.resetUploadDocumentStore}
              setProcedureInstance={setProcedureInstance}
              victims={victimsWithIds}
              vehicules={vehiculesWithIds}
              details={detailsAppealAdd}
              setDetails={setDetailsAppealAdd}
              processInformationValidation={
                processInformationValidationAppealAdd
              }
              messageError={messageErrorAppealAdd}
              civilPartyDocuments={civilPartyDocumentsAppealAdd}
              setCivilPartyDocuments={setCivilPartyDocumentsAppealAdd}
              canStartCivilParty={areProcedureInformationValidatedAppealAdd}
              judgementAddDocuments={appealAddDocuments}
              setJudgementAddDocuments={setAppealAddDocuments}
              disabled={
                !props.isCivilProceeding
                  ? !areProcedureInformationValidatedAppealAdd
                  : isCivilPartyPresentAppealAdd
              }
              requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
            />
          );
        case translate(messages.expertReportAppealLabelTitle, props):
          return (
            <ExpertReportAppealForm
              procedureInstance={procedureInstance}
              listings={props.listings}
              policyInfo={props.policyInfo}
              uploadDocument={props.uploadDocument}
              uploadedDocument={props.uploadedDocument}
              resetUploadDocumentStore={props.resetUploadDocumentStore}
              judgementAddDocuments={get(
                procedureInstance,
                'judgementAddAppealDocuments',
                [],
              )}
              expertiseReport={get(procedureInstance, 'expertReportAppeal', {})}
              setProcedureInstance={setProcedureInstance}
              documentId={props.documentId}
              setDocumentId={props.setDocumentId}
              requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
            />
          );
        case translate(messages.appealOnMeritsLabelTitle, props):
          return (
            <JudgementOnMeritsAppealForm
              listings={props.listings}
              policyInfo={props.policyInfo}
              judgementAddDocuments={get(
                procedureInstance,
                'judgementAddDocuments',
                [],
              )}
              judgementOnMerits={get(
                procedureInstance,
                'judgementOnMeritsAppealInfo',
                [],
              )}
              claimId={get(procedureInstance, 'claimId')}
              claimNumber={get(procedureInstance, 'claimNumber')}
              uploadDocument={props.uploadDocument}
              uploadedDocument={props.uploadedDocument}
              resetUploadDocumentStore={props.resetUploadDocumentStore}
              setDocumentId={props.setDocumentId}
              documentId={props.documentId}
              judgementOnMeritsInfo={judgementOnMeritsAppealInfo}
              setJudgementOnMeritsInfo={setJudgementOnMeritsAppealInfo}
              procedureNatureId={get(procedureInstance, 'procedureNatureId')}
              judgementAdd={civilPartyDocuments}
              procedureInstance={procedureInstance}
              victims={victimsWithIds}
              vehicules={vehiculesWithIds}
              expertReport={{
                vehiculeExpertises,
                victimExpertises,
              }}
              requestGetDocByDocumentumId={props.requestGetDocByDocumentumId}
            />
          );
      }
    });
  };

  const resetState = () => {
    pushTo(PAGES.FEE_NOTE)();
  };

  const judgementAddRequest = {
    isManager: false,
    isLawyer: true,
    step: PROCEDURE_STEPS.JUDGEMENT_ADD,
    status: PROCEDURE_STATUSES.PENDING,
    details,
    judgementAddDocuments,
    civilDocuments: [...civilPartyDocuments],
    username: currentIntervenantStorage.get()?.preferred_username,
    isInboxesActive: featureService.hasFeature(
      FEATURES.ATTRIBUTION_INBOXES_MANAGER,
    ),
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    lawyerCode: currentIntervenantStorage.get()?.sagis_code,
    procedureType: get(procedureInstance, 'procedureType')
      ? PROCEDURE_TYPES.APPEAL
      : PROCEDURE_TYPES.FIRST_INSTANCE,
    isCivilProceeding: get(procedureInstance, 'procedureNatureId') !== 3,
    isCompanyIntervenants: [],
    invoiceFeeNoteInfos: [],
    lawyerFirmReference: lawyerFirmRef,
    decisionGenerationFeeNote: decisionGenerationFeeNote,
  };

  const commonJomRequest = {
    jom: {
      ...judgementOnMeritsInfo,
      judgementNumber: formatCourtFileNumber(
        get(judgementOnMeritsInfo, 'judgementNumber'),
      ),
      lawyerCode: currentIntervenantStorage.get()?.sagis_code,
    },
    isInboxesActive: featureService.hasFeature(
      FEATURES.ATTRIBUTION_INBOXES_MANAGER,
    ),
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    claimId: get(procedureInstance, 'claimId'),
    isJudiciaryProcessCorrectional:
      get(procedureInstance, 'procedureNatureId') === 3,
    isCivilJudgementDefined:
      isCivilJudgementDefined(
        get(procedureInstance, 'judgementAddDocuments', []),
      ) ||
      isCivilJudgementDefined(
        get(procedureInstance, 'judgementAddAppealDocuments', []),
      ),
    claimNumber: get(procedureInstance, 'claimNumber'),
    sagisEvaluations: [],
    username: currentIntervenantStorage.get()?.preferred_username,
    isLawyer: true,
  };

  const appealAddRequest = {};

  const commonFeeNoteRequest = {
    codeIntervenant: currentIntervenantStorage.get()?.sagis_code,
    firmReference: lawyerFirmRef,
    courtNumber: `${resolveJudiciaryProcessType(
      get(procedureInstance, 'procedureNatureId'),
    )} ${formatCourtFileNumber(get(details, 'courtFile'))}`,
    invoiceFeeNote:
      featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
      !isAllValuesIsNil({
        invoiceNumber: invoiceNumber,
        invoiceDate: invoiceDate,
        ...invoiceDocument,
      })
        ? {
            codeIntervenant: currentIntervenantStorage.get()?.sagis_code,
            createdBy: currentIntervenantStorage?.get()?.preferred_username,
            invoiceNumber: invoiceNumber,
            invoiceDate: invoiceDate === 'Invalid date' ? null : invoiceDate,
            receptionDate: moment().format('YYYY-MM-DD'),
            ...invoiceDocument,
            isIndexed: false,
          }
        : undefined,
  };

  const handleValidate = (withFeeNote: boolean) => {
    return props.requestPreOpenBasicClaim(
      {
        claimToPreOpen: {
          ...get(location, 'state.claimToPreOpen'),
          victims: get(location, 'state.claimToPreOpen.victims', []).map(
            (el: any) => {
              return {
                ...el,
                id: get(
                  victimsWithIds.find((v: any) => v.cin === el.cin),
                  'id',
                ),
              };
            },
          ),
          vehicules: get(location, 'state.claimToPreOpen.vehicules', []).map(
            (el: any) => {
              return {
                ...el,
                id: get(
                  victimsWithIds.find(
                    (v: any) => v.registrationNumber === el.registrationNumber,
                  ),
                  'id',
                ),
              };
            },
          ),
          createdBy: get(
            currentIntervenantStorage?.get(),
            'preferred_username',
            null,
          ),
        },
        assignment: {
          assignedLawyer: currentIntervenantStorage.get()?.sagis_code,
        },
        judgementAdd: {
          ...judgementAddRequest,
          invoiceFeeNoteInfos: [],
          feeNote: withFeeNote
            ? {
                step: PROCEDURE_STEPS.JUDGEMENT_ADD,
                judgementDate: get(
                  judgementAddDocuments,
                  '[0].judgementDate',
                  '',
                ),
                ...commonFeeNoteRequest,
              }
            : undefined,
        },
        appealAdd: {
          ...appealAddRequest,
          invoiceFeeNoteInfos: [],
          feeNote: withFeeNote
            ? {
                step: PROCEDURE_STEPS.ARRET_ADD,
                judgementDate: get(
                  judgementAddDocuments,
                  '[0].judgementDate',
                  '',
                ),
                ...commonFeeNoteRequest,
              }
            : undefined,
        },
        expertReport: {
          isLawyer: true,
          isInboxesActive: featureService.hasFeature(
            FEATURES.ATTRIBUTION_INBOXES_MANAGER,
          ),
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
          expertReportToAdd: expertReportState,
          claimId: get(procedureInstance, 'claimId'),
          claimNumber: get(procedureInstance, 'claimNumber'),
          procedureType: PROCEDURE_TYPES.FIRST_INSTANCE,
          step: LEGAL_PROCEEDINGS_STEPS.EXPERT_REPORT,
          status: '',
          judiciaryProcessId: get(procedureInstance, 'judicialProcedureId'),
        },
        expertReportAppeal: {
          isLawyer: true,
          isInboxesActive: featureService.hasFeature(
            FEATURES.ATTRIBUTION_INBOXES_MANAGER,
          ),
          isTrackingActive: featureService.hasFeature(
            FEATURES.TRACKING_ACTIONS_USER,
          ),
          victimExpertise: {
            ...get(
              expertReportState,
              '[0].expertises[0].victims[0].victimExpertise', // TO CHANGE
              {},
            ),
          },
          vehiculeExpertise: {
            ...get(
              expertReportState,
              '[0].expertises[0].vehicules[0].vehiculeExpertise',
              {},
            ),
          },
          claimId: get(procedureInstance, 'claimId'),
          claimNumber: get(procedureInstance, 'claimNumber'),
          procedureType: PROCEDURE_TYPES.APPEAL,
          step: LEGAL_PROCEEDINGS_STEPS.EXPERT_REPORT,
          status: '',
          judiciaryProcessId: get(procedureInstance, 'judicialProcedureId'),
        },
        jomRequest: {
          ...commonJomRequest,
          feeNote: withFeeNote
            ? {
                step: PROCEDURE_STEPS.ARRET_ON_MERITS,
                codeIntervenant: currentIntervenantStorage.get()?.sagis_code,
                judgementDate: get(judgementOnMeritsInfo, 'judgementDate', ''),
                firmReference: lawyerFirmRef,
                courtNumber: `${resolveJudiciaryProcessType(
                  get(procedureInstance, 'procedureNatureId'),
                )} ${formatCourtFileNumber(
                  get(judgementOnMeritsInfo, 'judgementNumber'),
                )}`,
                invoiceFeeNote:
                  featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
                  !isAllValuesIsNil({
                    invoiceNumber: invoiceNumber,
                    invoiceDate: invoiceDate,
                    ...invoiceDocument,
                  })
                    ? {
                        codeIntervenant: currentIntervenantStorage.get()
                          ?.sagis_code,
                        createdBy:
                          currentIntervenantStorage?.get() &&
                          currentIntervenantStorage?.get()?.preferred_username,
                        invoiceNumber: invoiceNumber,
                        invoiceDate:
                          invoiceDate === 'Invalid date' ? null : invoiceDate,
                        receptionDate: moment().format('YYYY-MM-DD'),
                        ...invoiceDocument,
                        isIndexed: false,
                      }
                    : undefined,
              }
            : undefined,
          procedureType: PROCEDURE_TYPES.FIRST_INSTANCE,
        },
        jomAppealRequest: {
          ...commonJomRequest,
          feeNote: withFeeNote
            ? {
                step: PROCEDURE_STEPS.ARRET_ON_MERITS,
                codeIntervenant: currentIntervenantStorage.get()?.sagis_code,
                judgementDate: get(judgementOnMeritsInfo, 'judgementDate', ''),
                firmReference: lawyerFirmRef,
                courtNumber: `${resolveJudiciaryProcessType(
                  get(procedureInstance, 'procedureNatureId'),
                )} ${formatCourtFileNumber(
                  get(judgementOnMeritsInfo, 'judgementNumber'),
                )}`,
                invoiceFeeNote:
                  featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) &&
                  !isAllValuesIsNil({
                    invoiceNumber: invoiceNumber,
                    invoiceDate: invoiceDate,
                    ...invoiceDocument,
                  })
                    ? {
                        codeIntervenant: currentIntervenantStorage.get()
                          ?.sagis_code,
                        createdBy:
                          currentIntervenantStorage?.get() &&
                          currentIntervenantStorage?.get()?.preferred_username,
                        invoiceNumber: invoiceNumber,
                        invoiceDate:
                          invoiceDate === 'Invalid date' ? null : invoiceDate,
                        receptionDate: moment().format('YYYY-MM-DD'),
                        ...invoiceDocument,
                        isIndexed: false,
                      }
                    : undefined,
              }
            : undefined,
          procedureType: PROCEDURE_TYPES.APPEAL,
        },
        isLawyerInboxesActive: featureService.hasFeature(
          FEATURES.LAWYER_INBOXES,
        ),
      },
      resetState,
    );
  };

  return (
    <>
      <Stepper isBasicManagement />
      <Container>
        <div style={{ margin: '30px' }}>
          <ToggleTabs
            currentTabIndex={currentTabIndex}
            labels={[...judiciaryProcesslabels]}
            isBasicManagement
          >
            {judiciaryProcessResolver(mappedLabels)}
          </ToggleTabs>
        </div>
        <NavigationButtons
          isRecap={currentTabIndex === mappedLabels.length - 1}
          forwardClick={() =>
            currentTabIndex < mappedLabels.length - 1
              ? setCurrentTabIndex(currentTabIndex + 1)
              : setIsSuccessPopinOpen(true)
          }
          onPreviousClick={() =>
            currentTabIndex > 0
              ? setCurrentTabIndex(currentTabIndex - 1)
              : pushTo(PAGES.RECAPITULATIF, {
                  isFromSagis: true,
                  procedureNatureId: get(
                    props,
                    'location.state.procedureNatureId',
                  ),
                  procedureType: get(props, 'location.state.procedureType'),
                  judicialProcedureId: get(
                    props,
                    'location.state.judicialProcedureId',
                  ),
                })()
          }
        />
      </Container>
      {featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) && (
        <InvoiceFeeNoteDialog
          open={isSuccessPopinOpen}
          onClose={() => setIsSuccessPopinOpen(false)}
          decisionGenerationFeeNote={decisionGenerationFeeNote}
          setDecisionGenerationFeeNote={setDecisionGenerationFeeNote}
          handleValidate={
            decisionGenerationFeeNote
              ? handleOpenInvoicesInfoDialog
              : handleCloseSuccessDialog
          }
          shouldDisplayFirmReferenceField
          setLawyerFirmRef={setLawyerFirmRef}
          lawyerFirmRef={lawyerFirmRef}
        />
      )}
      {featureService.hasFeature(FEATURES.BILLING_INFO_FEE_NOTE) && (
        <InvoiceInfoDialog
          open={openInvoiceFeeNoteInfoDialog}
          onClose={handleCloseInvoiceFeeNote}
          handleValidate={handleValidateInvoice}
          invoiceNumber={invoiceNumber}
          invoiceDate={invoiceDate}
          setInvoiceNumber={setInvoiceNumber}
          setInvoiceDate={setInvoiceDate}
          resetUploadedDocument={props.resetUploadDocumentStore}
          uploadDocument={props.uploadDocument}
          uploadedDocument={props.uploadedDocument}
          invoiceDocument={invoiceDocument}
          setInvoiceDocument={setInvoiceDocument}
        />
      )}
    </>
  );
};

const mapDispatchToProps = {
  uploadDocument,
  resetUploadDocumentStore,
  requestPreOpenBasicClaim,
  setDocumentId,
};

const mapStateToProps = createStructuredSelector({
  listings: makeSelectListings(),
  uploadedDocument: makeSelectDocument(),
  storedClaimToPreopen: makeSelecClaimToPreopen(),
  policyInfo: makeSelectSearchClaims(),
  documentId: makeSelectDocumentId(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(JudiciaryProcessPage));
